import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatIST } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import AssignOperatorDialog from '@/components/AssignOperatorDialog';
import DivideBatchStrip from '@/components/DivideBatchStrip';
import { SplitBadge } from '@/components/BatchSplitIndicator';
import BatchHistoryPanel from './OrderDetail/BatchHistoryPanel';

import api, { advanceBatches } from '@/lib/api';
import { DEPT_NAMES } from '@/lib/constants';
import { toast } from 'sonner';
import {
  Factory, CheckCircle2, Eye, Search, UserPlus, Package, Truck, ArrowRight, X,
  Split, ChevronsRight,
} from 'lucide-react';

import { Screen, ScreenSplit, Pane, DataGrid, FactBand, Fact, CellButton, NumberCell, TextCell } from '@/components/data';
import { PRIORITY_MAP } from '@/lib/status-colors';
import { needsPlate, plateStatus as plateStatusOf } from '@/lib/plate';
import PhoneBoard from './Production/PhoneBoard';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * The floor, as one flat list of batches.
 *
 * A batch is what the floor actually schedules against — it is the thing that
 * stands at a stage, gets a machine, and moves. The old screen buried it three
 * levels down: order card → expand → item block → stage strip → plate panel →
 * batch row, with the whole 80-line batch block written twice, once for split
 * batches and once for the rest. You could not see two orders' batches at the
 * same stage without expanding both.
 *
 * Now every batch on the floor is a row. Closing a stage happens in a strip
 * under the row rather than in a dialog: same five fields, no context switch,
 * and the split warning appears as you type rather than after you commit.
 *
 * Because the row is a batch, the two things batch processing needs fall out of
 * the same list: tick several rows and advance them together, or divide one row
 * into the runs the job will actually be made in. Both are supervisor work, so
 * a receptionist reading the board sees neither.
 */

const PRIORITY_TEXT = { high: 'text-red-600', medium: 'text-amber-600', low: 'text-emerald-600' };

const BATCH_STATUS_TEXT = {
  pending: 'text-slate-500',
  in_progress: 'text-blue-600',
  completed: 'text-emerald-600',
};

const EMPTY_FORM = { qty_completed: '', qty_rejected: '0', operator: '', machine_used: '', remarks: '' };

const BATCH_PROCESSING_ROLES = ['admin', 'supervisor'];

export default function ProductionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const canBatchProcess = (user?.roles || [user?.role])
    .some(r => BATCH_PROCESSING_ROLES.includes(r));
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, high_priority: 0 });
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRowId, setSelectedRowId] = useState(null);

  // the batch whose stage is being closed, and the numbers being typed for it
  const [completing, setCompleting] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [assignModal, setAssignModal] = useState(null);
  const [plateSubmitting, setPlateSubmitting] = useState(null);

  // batch processing: the ticked rows, and the row being cut into runs
  const [picked, setPicked] = useState(() => new Set());
  const [dividing, setDividing] = useState(null);
  const [advancing, setAdvancing] = useState(false);

  const fetchStages = useCallback(async () => {
    try {
      const res = await api.get('/production/stages');
      setStages(res.data);
    } catch {}
  }, []);

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const params = { limit: 100 };
      if (selectedStage) params.stage = selectedStage;
      if (searchQuery) params.search = searchQuery;
      const res = await api.get('/production/orders', { params });
      setOrders(res.data.orders);
      setStats(res.data.stats);
    } catch {
      toast.error('Failed to load production orders');
    }
    setOrdersLoading(false);
  }, [selectedStage, searchQuery]);

  useEffect(() => {
    fetchStages().then(() => setLoading(false));
  }, [fetchStages]);

  useEffect(() => {
    if (!loading) fetchOrders();
  }, [fetchOrders, loading]);

  const handleMarkPlateOrdered = async (orderId, lineItemId) => {
    setPlateSubmitting(lineItemId);
    try {
      const res = await api.post(`/plates/orders/${orderId}/items/${lineItemId}/mark-ordered`);
      toast.success(res?.data?.message || 'Plate marked as ordered');
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark plate as ordered');
    }
    setPlateSubmitting(null);
  };

  const handleMarkPlateReceived = async (orderId, lineItemId) => {
    setPlateSubmitting(lineItemId);
    try {
      const res = await api.post(`/plates/orders/${orderId}/items/${lineItemId}/mark-received`);
      toast.success(res?.data?.message || 'Plate received');
      fetchOrders();
      fetchStages();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark plate as received');
    }
    setPlateSubmitting(null);
  };

  const startComplete = (row) => {
    setCompleting(row.id);
    setSelectedRowId(row.id);
    setForm({ ...EMPTY_FORM, qty_completed: String(row.batch.quantity) });
  };

  const cancelComplete = () => { setCompleting(null); setForm(EMPTY_FORM); };

  const submitComplete = async (row) => {
    const qty = parseInt(form.qty_completed) || 0;
    if (qty <= 0) { toast.error('Quantity must be positive'); return; }
    if (qty > row.batch.quantity) {
      toast.error(`Cannot complete more than batch quantity (${row.batch.quantity})`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(
        `/production/orders/${row.order.id}/batches/${row.batch.id}/complete`,
        {
          qty_completed: qty,
          qty_rejected: parseInt(form.qty_rejected) || 0,
          operator: form.operator || null,
          machine_used: form.machine_used || null,
          remarks: form.remarks || null,
        }
      );
      if (res.data.split) {
        toast.success(`Stage completed — batch split: ${qty} advanced, ${row.batch.quantity - qty} remaining`);
      } else {
        toast.success('Stage completed successfully');
      }
      if (res.data.all_items_complete) toast.info('All items complete — order ready for dispatch!');
      cancelComplete();
      fetchOrders();
      fetchStages();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to complete stage');
    }
    setSubmitting(false);
  };

  const totalPending = stages.reduce((sum, s) => sum + s.count, 0);

  // 54 stages in a flat list is a wall to read; the seven categories are how the
  // floor is laid out anyway, so the bar follows them.
  const stagesByCategory = stages.reduce((groups, stage) => {
    const category = stage.category || 'Other';
    (groups[category] = groups[category] || []).push(stage);
    return groups;
  }, {});

  const currentAssignment = (batch) =>
    (batch.stage_assignments || []).find(a => a.stage_name === batch.current_stage_name);

  // Every batch on the floor, flattened out of the order → item → batch nesting
  // the API returns. This list is the screen.
  const batchRows = useMemo(() => {
    const out = [];
    for (const order of orders) {
      for (const item of order.line_items || []) {
        const plateRequired = needsPlate(item);
        const plateStatus = plateRequired ? plateStatusOf(item) : null;
        for (const batch of item.batches || []) {
          out.push({
            id: batch.id,
            order,
            item,
            batch,
            plateRequired,
            plateStatus,
            atPlateMaking: batch.status === 'in_progress'
              && item.production_stages?.[batch.current_stage_index]?.stage_key === 'plate_making',
          });
        }
      }
    }
    return out;
  }, [orders]);

  // A batch can only be advanced from a stage it is actually standing at, and a
  // batch waiting on a plate is blocked at the machine, not by the office.
  const movableRows = useMemo(
    () => batchRows.filter(r => r.batch.status === 'in_progress' && !r.atPlateMaking),
    [batchRows]
  );

  const pickedRows = useMemo(
    () => movableRows.filter(r => picked.has(r.id)),
    [movableRows, picked]
  );
  const pickedPieces = pickedRows.reduce((sum, r) => sum + (r.batch.quantity || 0), 0);
  const allPicked = movableRows.length > 0 && pickedRows.length === movableRows.length;

  const togglePicked = (batchId) => setPicked(prev => {
    const next = new Set(prev);
    if (!next.delete(batchId)) next.add(batchId);
    return next;
  });

  // Advancing rewrites the whole line_items array per order, so this is one call
  // rather than a loop — two in flight against one order would lose each other.
  const advancePicked = async () => {
    setAdvancing(true);
    try {
      // pickedRows, not the raw set: a refetch can retire a ticked batch
      const res = await advanceBatches({ batch_ids: pickedRows.map(r => r.batch.id) });
      toast.success(res.message);
      (res.failed || []).forEach(f => toast.error(f.reason));
      setPicked(new Set());
      fetchOrders();
      fetchStages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to advance the selected batches');
    }
    setAdvancing(false);
  };

  // The completion and divide strips are spliced in directly beneath the row
  // they belong to.
  const rows = useMemo(() => {
    const out = [];
    for (const row of batchRows) {
      out.push(row);
      if (completing === row.id) {
        out.push({ id: `${row.id}:complete`, __full: true, __render: () => renderCompleteStrip(row) });
      }
      if (dividing === row.id) {
        out.push({
          id: `${row.id}:divide`,
          __full: true,
          __render: () => (
            <DivideBatchStrip
              batch={row.batch}
              onCancel={() => setDividing(null)}
              onDone={() => { setDividing(null); fetchOrders(); fetchStages(); }}
            />
          ),
        });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchRows, completing, dividing, form, submitting]);

  const renderCompleteStrip = (row) => {
    const qty = parseInt(form.qty_completed) || 0;
    const willSplit = qty > 0 && qty < row.batch.quantity;
    return (
      <div className="flex flex-wrap items-end gap-2" data-testid="complete-stage-strip">
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-brand-blue">
          Close {row.batch.current_stage_name}
        </span>
        {[
          ['Done', 'qty_completed', 'number', '5rem'],
          ['Rejected', 'qty_rejected', 'number', '5rem'],
          ['Operator', 'operator', 'text', '8rem'],
          ['Machine', 'machine_used', 'text', '8rem'],
          ['Remarks', 'remarks', 'text', '14rem'],
        ].map(([label, key, type, width]) => (
          <label key={key} className="flex flex-col gap-0.5" style={{ width }}>
            <span className="text-[9px] font-medium uppercase tracking-wider text-slate-500">{label}</span>
            <span className="rounded border border-slate-300 bg-white">
              {type === 'number' ? (
                <NumberCell
                  label={label}
                  value={form[key]}
                  onChange={(v) => setForm(f => ({ ...f, [key]: v }))}
                  max={key === 'qty_completed' ? row.batch.quantity : undefined}
                />
              ) : (
                <TextCell
                  label={label}
                  value={form[key]}
                  onChange={(v) => setForm(f => ({ ...f, [key]: v }))}
                />
              )}
            </span>
          </label>
        ))}
        <Button
          size="sm"
          className="h-7 bg-green-600 text-white hover:bg-green-700"
          disabled={submitting}
          onClick={() => submitComplete(row)}
          data-testid="confirm-complete-stage-btn"
        >
          <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
          {submitting ? 'Saving…' : 'Done'}
        </Button>
        <Button size="sm" variant="ghost" className="h-7" onClick={cancelComplete}>
          <X className="h-3.5 w-3.5" />
        </Button>
        {willSplit && (
          <span className="w-full text-[11px] text-amber-700">
            Batch will split: {qty} advances, {row.batch.quantity - qty} stays at {row.batch.current_stage_name}.
          </span>
        )}
      </div>
    );
  };

  const columns = useMemo(() => ([
    ...(canBatchProcess ? [{
      key: 'pick',
      width: '2.25rem',
      header: (
        <input
          type="checkbox"
          checked={allPicked}
          onChange={() => setPicked(allPicked ? new Set() : new Set(movableRows.map(r => r.id)))}
          aria-label={`Select all ${movableRows.length} movable batches`}
          disabled={movableRows.length === 0}
          className="h-3.5 w-3.5 cursor-pointer accent-brand-blue"
          data-testid="pick-all-batches"
        />
      ),
      render: (row) => (
        row.batch.status === 'in_progress' && !row.atPlateMaking ? (
          <input
            type="checkbox"
            checked={picked.has(row.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => togglePicked(row.id)}
            aria-label={`Select batch #${row.batch.batch_number} of ${row.item.item_name || row.order.order_id}`}
            className="h-3.5 w-3.5 cursor-pointer accent-brand-blue"
            data-testid={`pick-batch-${row.batch.id}`}
          />
        ) : null
      ),
      total: () => '',
    }] : []),
    {
      key: 'item',
      header: 'Item / Customer',
      width: 'minmax(13rem,1.4fr)',
      total: () => 'Total',
      render: (row) => (
        <>
          <span className="block truncate font-semibold text-slate-900">
            {row.item.item_name || row.item.name || row.item.product_subtype || 'Item'}
          </span>
          <span className="block truncate text-[11px] leading-tight text-slate-500">
            {row.order.customer_name}{row.order.firm_name ? ` — ${row.order.firm_name}` : ''}
          </span>
        </>
      ),
    },
    {
      key: 'order',
      header: 'Order',
      width: '9.5rem',
      render: (row) => (
        <span className="block truncate text-[11px] text-slate-400">{row.order.order_id}</span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      width: '6.5rem',
      render: (row) => (
        <span className="block truncate text-[11px] text-slate-600">
          {DEPT_NAMES[row.item.order_type] || row.item.order_type}
        </span>
      ),
    },
    {
      key: 'batch',
      header: 'Batch',
      width: '6.5rem',
      render: (row) => (
        <span className="flex items-center gap-1">
          <span className="text-[11px] text-slate-500">#{row.batch.batch_number}</span>
          <SplitBadge batch={row.batch} />
        </span>
      ),
    },
    {
      key: 'qty',
      header: 'Qty',
      align: 'right',
      width: '5rem',
      render: (row) => <span className="font-medium">{row.batch.quantity?.toLocaleString()}</span>,
      total: (list) => list
        .filter(r => !r.__full)
        .reduce((sum, r) => sum + (r.batch?.quantity || 0), 0)
        .toLocaleString(),
    },
    {
      key: 'stage',
      header: 'Stage',
      width: '10rem',
      render: (row) => (
        <span className={`block truncate text-[12px] font-medium ${BATCH_STATUS_TEXT[row.batch.status] || 'text-slate-500'}`}>
          {row.batch.status === 'completed' ? 'Completed' : row.batch.current_stage_name}
        </span>
      ),
    },
    {
      key: 'machine',
      header: 'Machine',
      width: '9rem',
      render: (row) => {
        const assignment = currentAssignment(row.batch);
        if (row.batch.status === 'completed' || row.atPlateMaking) {
          return <span className="text-[11px] text-slate-400">{assignment?.machine_name || '—'}</span>;
        }
        return (
          <CellButton
            icon={UserPlus}
            label={assignment?.machine_name || 'Assign'}
            tone={assignment ? 'default' : 'accent'}
            onClick={() => setAssignModal({ batch: row.batch, order: row.order, lineItem: row.item })}
          />
        );
      },
    },
    {
      key: 'priority',
      header: 'Priority',
      width: '5.5rem',
      render: (row) => (
        <span className={`text-[11px] font-medium ${PRIORITY_TEXT[row.order.priority] || 'text-slate-500'}`}>
          {PRIORITY_MAP[row.order.priority]?.label || row.order.priority}
        </span>
      ),
    },
    {
      key: 'due',
      header: 'Due',
      width: '6.5rem',
      render: (row) => (
        <span className="whitespace-nowrap text-[11px] text-slate-600">
          {row.order.expected_delivery_date ? formatIST(row.order.expected_delivery_date, { dateOnly: true }) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '13rem',
      align: 'right',
      render: (row) => (
        <span className="flex items-center justify-end gap-0.5">
          {row.atPlateMaking ? (
            <>
              {row.plateStatus !== 'ordered' && row.plateStatus !== 'received' && (
                <CellButton
                  icon={Package}
                  label="Plate ordered"
                  disabled={plateSubmitting === row.item.id}
                  onClick={() => handleMarkPlateOrdered(row.order.id, row.item.id)}
                />
              )}
              {row.plateStatus !== 'received' && (
                <CellButton
                  icon={Truck}
                  label="Plate received"
                  tone="good"
                  disabled={plateSubmitting === row.item.id}
                  onClick={() => handleMarkPlateReceived(row.order.id, row.item.id)}
                />
              )}
            </>
          ) : row.batch.status === 'in_progress' && completing !== row.id ? (
            <CellButton
              icon={CheckCircle2}
              label="Complete"
              tone="good"
              onClick={() => startComplete(row)}
              testId={`complete-batch-${row.batch.id}`}
            />
          ) : null}
          {canBatchProcess && row.batch.status !== 'completed'
            && row.batch.quantity > 1 && dividing !== row.id && (
            <CellButton
              icon={Split}
              label="Divide"
              onClick={() => { setDividing(row.id); setCompleting(null); }}
              testId={`divide-batch-${row.batch.id}`}
            />
          )}
          <CellButton
            icon={Eye}
            label="Open order"
            iconOnly
            onClick={() => navigate(`/orders/${row.order.id}`)}
          />
        </span>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [completing, dividing, plateSubmitting, navigate, canBatchProcess, picked, allPicked, movableRows]);

  const selected = batchRows.find(r => r.id === selectedRowId);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-blue" />
      </div>
    );
  }

  const assignDialog = assignModal && (
    <AssignOperatorDialog
      batch={assignModal.batch}
      order={assignModal.order}
      lineItem={assignModal.lineItem}
      onClose={() => setAssignModal(null)}
      onSuccess={() => {
        toast.success('Machine assigned successfully');
        fetchOrders();
      }}
    />
  );

  // one card per batch below lg; the same numbers close a stage, the same call moves the ticked ones
  if (isMobile) {
    return (
      <>
        <PhoneBoard
          stagesByCategory={stagesByCategory} selectedStage={selectedStage}
          onStage={(name) => { setSelectedStage(name); setSelectedRowId(null); }}
          stats={stats} totalPending={totalPending}
          rows={batchRows} loading={ordersLoading} onSearch={setSearchQuery} searchQuery={searchQuery}
          expandedId={selectedRowId}
          onToggle={(row) => setSelectedRowId(row.id === selectedRowId ? null : row.id)}
          assignmentOf={currentAssignment}
          canBatchProcess={canBatchProcess} picked={picked} movableRows={movableRows}
          pickedRows={pickedRows} pickedPieces={pickedPieces} allPicked={allPicked}
          onTogglePick={togglePicked}
          onPickAll={() => setPicked(allPicked ? new Set() : new Set(movableRows.map(r => r.id)))}
          onClearPicked={() => setPicked(new Set())}
          onAdvance={advancePicked} advancing={advancing}
          completing={completing} form={form}
          onFormChange={(key, value) => setForm(f => ({ ...f, [key]: value }))}
          onStartComplete={startComplete} onCancelComplete={cancelComplete}
          onSubmitComplete={submitComplete} submitting={submitting}
          dividing={dividing}
          onStartDivide={(row) => { setDividing(row.id); setCompleting(null); }}
          onCancelDivide={() => setDividing(null)}
          onDivided={() => { setDividing(null); fetchOrders(); fetchStages(); }}
          onAssign={(row) => setAssignModal({ batch: row.batch, order: row.order, lineItem: row.item })}
          plateSubmitting={plateSubmitting}
          onPlateOrdered={(row) => handleMarkPlateOrdered(row.order.id, row.item.id)}
          onPlateReceived={(row) => handleMarkPlateReceived(row.order.id, row.item.id)}
          onOpenOrder={(row) => navigate(`/orders/${row.order.id}`)}
        />
        {assignDialog}
      </>
    );
  }

  return (
    <Screen data-testid="production-page">
      {/* Stage bar — the whole floor in one strip, scrolled sideways */}
      <div
        className="flex min-w-0 shrink-0 items-stretch overflow-hidden rounded-lg border border-slate-200 bg-white shadow-card"
        data-testid="stage-bar"
      >
        <button
          onClick={() => { setSelectedStage(null); setSelectedRowId(null); }}
          className={`flex flex-shrink-0 items-center gap-2 border-r border-slate-200 px-3 text-sm transition-colors
            ${selectedStage === null ? 'bg-brand-blue font-medium text-white' : 'text-slate-700 hover:bg-slate-50'}`}
          data-testid="stage-all"
        >
          <span>All Stages</span>
          <Badge
            variant={selectedStage === null ? 'secondary' : 'outline'}
            className={`text-[10px] ${selectedStage === null ? 'border-white/30 bg-white/20 text-white' : ''}`}
          >
            {stats.total}
          </Badge>
        </button>

        <div className="min-w-0 flex-1 overflow-x-auto">
          <div className="flex w-max items-center gap-1 px-2 py-1.5">
            {Object.entries(stagesByCategory).map(([category, groupStages]) => (
              <div key={category} className="mr-1 flex items-center gap-1 border-r border-slate-100 pr-2 last:border-r-0">
                <span className="whitespace-nowrap px-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {category}
                </span>
                {groupStages.map((stage) => (
                  <button
                    key={stage.name}
                    onClick={() => { setSelectedStage(stage.name); setSelectedRowId(null); }}
                    className={`flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1 text-sm transition-colors
                      ${selectedStage === stage.name ? 'bg-brand-blue font-medium text-white'
                        : stage.count > 0 ? 'text-slate-700 hover:bg-slate-50' : 'text-slate-400 hover:bg-slate-50'}`}
                    data-testid={`stage-${stage.name}`}
                  >
                    <span>{stage.name}</span>
                    {stage.count > 0 && (
                      <Badge
                        variant={selectedStage === stage.name ? 'secondary' : 'outline'}
                        className={`text-[10px] ${selectedStage === stage.name ? 'border-white/30 bg-white/20 text-white' : ''}`}
                      >
                        {stage.count}
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-1.5 shadow-card">
        <div className="min-w-0">
          <h1 className="truncate font-heading text-base font-bold text-slate-900">
            {selectedStage || 'All Production Orders'}
          </h1>
          <p className="text-[11px] text-slate-400">
            {batchRows.length} batch{batchRows.length !== 1 ? 'es' : ''} listed
            {selectedStage && ' at this stage'}
            {' · '}{totalPending} active batch{totalPending !== 1 ? 'es' : ''} on the floor
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pickedRows.length > 0 && (
            <div
              className="flex items-center gap-2 rounded-lg border border-brand-blue/30 bg-blue-50 px-2 py-1"
              data-testid="batch-selection-bar"
            >
              <span className="whitespace-nowrap text-[11px] font-medium text-brand-blue">
                {pickedRows.length} batch{pickedRows.length !== 1 ? 'es' : ''} · {pickedPieces.toLocaleString()} pcs
              </span>
              <Button
                size="sm"
                className="h-6 bg-brand-blue text-white hover:bg-brand-blue-dark"
                disabled={advancing}
                onClick={advancePicked}
                data-testid="advance-picked-batches"
              >
                <ChevronsRight className="mr-1 h-3.5 w-3.5" />
                {advancing ? 'Advancing…' : 'Advance to next stage'}
              </Button>
              <button
                type="button"
                onClick={() => setPicked(new Set())}
                aria-label="Clear batch selection"
                className="rounded p-0.5 text-slate-400 hover:bg-white hover:text-slate-700"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <div className="relative w-full sm:w-64">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              className="h-7 pl-8 text-xs"
              placeholder="Search orders…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="production-search"
            />
          </div>
        </div>
      </div>

      <ScreenSplit
        railWidth="19rem"
        rail={selected ? (
          <Pane
            title={`Batch #${selected.batch.batch_number}`}
            aside={selected.item.item_name || selected.item.order_type}
            actions={
              <button
                type="button"
                onClick={() => setSelectedRowId(null)}
                aria-label="Close batch detail"
                className="rounded p-0.5 text-white/80 hover:bg-white/20"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            }
            className="max-h-full"
          >
            <div className="p-1.5">
              <FactBand min="100%" className="border-0 p-0 shadow-none">
                <Fact label="Order">{selected.order.order_id}</Fact>
                <Fact label="Customer">{selected.order.customer_name}</Fact>
                <Fact label="Qty">{selected.batch.quantity?.toLocaleString()}</Fact>
                <Fact label="Stage">{selected.batch.current_stage_name}</Fact>
                <Fact label="Machine">{currentAssignment(selected.batch)?.machine_name}</Fact>
                <Fact label="Status">{selected.batch.status?.replace('_', ' ')}</Fact>
                {selected.plateRequired && <Fact label="Plate">{selected.plateStatus}</Fact>}
              </FactBand>
            </div>

            {selected.item.production_stages?.length > 0 && (
              <div className="border-t border-slate-100 px-2 py-2">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Pipeline</p>
                <div className="flex flex-wrap items-center gap-1">
                  {selected.item.production_stages.map((stage, si) => {
                    const done = si < (selected.batch.current_stage_index || 0);
                    const here = si === (selected.batch.current_stage_index || 0);
                    return (
                      <React.Fragment key={si}>
                        <span className={`rounded-full border px-1.5 py-0.5 text-[10px] ${
                          done ? 'border-green-200 bg-green-50 text-green-700'
                            : here ? 'border-blue-200 bg-blue-50 font-medium text-blue-700'
                              : 'border-slate-200 bg-slate-50 text-slate-400'
                        }`}>
                          {stage.stage_name}
                        </span>
                        {si < selected.item.production_stages.length - 1 && (
                          <ArrowRight className="h-2.5 w-2.5 shrink-0 text-slate-300" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            <BatchHistoryPanel batch={selected.batch} />
          </Pane>
        ) : null}
      >
        <Pane
          title="Batches on the floor"
          aside={selectedStage || 'all stages'}
          scroll={false}
          bodyClassName="flex flex-col"
        >
          <DataGrid
            data-testid="production-batches"
            columns={columns}
            rows={rows}
            loading={ordersLoading}
            getRowId={(row) => row.id}
            selectedId={selectedRowId}
            onRowClick={(row) => !row.__full && setSelectedRowId(row.id === selectedRowId ? null : row.id)}
            showTotals={batchRows.length > 0}
            empty={
              <span className="flex flex-col items-center gap-2 text-slate-400">
                <Factory className="h-8 w-8 text-slate-300" />
                {searchQuery ? 'No orders match your search'
                  : selectedStage ? `Nothing at "${selectedStage}"`
                    : 'Nothing in production'}
              </span>
            }
          />
        </Pane>
      </ScreenSplit>

      {assignDialog}
    </Screen>
  );
}
